/**
 * E002-T040: workflow engine API Client
 *
 * sl-n8i1.3: Refactored to use workspace-scoped routes
 *
 * Client for communicating with workflow engine via Backend Proxy.
 * All workflow engine API calls go through the backend to keep credentials secure.
 *
 * Backend Endpoints (sl-n8i1.2):
 * - GET /api/workspace//workflow/status - Check if automation is configured
 * - GET /api/workspace//workflow/workflows - List all workflows
 * - GET /api/workspace//workflow/workflows/:id - Get specific workflow
 * - GET /api/workspace//workflow/workflows/:id/executions/latest - Get latest execution
 *
 * Config Endpoints (sl-n8i1.1):
 * - GET /api/workspace/:workspaceId/workflow-config - Get config status (masked)
 */

import {
  type AutomationSafetyDiagnosticPage,
  AutomationSafetyDiagnosticPageSchema,
} from '@streamlain/automation-safety-contract';
import { LiveData, Service } from '@toeverything/infra';

import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
  probeBackendAvailabilityNow,
} from '../../../utils/backend-availability';
import { defaultWorkspaceApiClient } from '../../cloud';
import type { WorkspaceService } from '../../workspace';
import type {
  ValidationReport,
  WorkflowVersion,
} from '../workflow-viewer/graph-types';
import type { SWRStepType } from '../workflow-viewer/swr-step-types';
import type {
  Category,
  ClassificationRuleDTO,
  ClassificationSuggestion,
  CompatibilityEvaluation,
  CompatibilityIssue,
  CopilotMigrationResult,
  DiscoveryResult,
  ImportOptions,
  ImportResult,
  PaginatedResponse,
  WorkflowContext,
  WorkflowEngineNode,
  WorkflowTemplate,
} from './marketplace-types';

// =============================================================================
// Types
// =============================================================================

export interface WorkflowOwner {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface EngineWorkflow {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  categories?: Array<{ id: string; name: string }>;
  nodes?: EngineNode[];
  owner?: WorkflowOwner;
  previewSet?: unknown;
}

export interface EngineNode {
  id: string;
  name: string;
  type: string;
  parameters?: Record<string, unknown>;
}

export interface WorkflowWithWebhook extends EngineWorkflow {
  hasWebhook: boolean;
  webhookPath?: string;
  webhookMethod?: string;
  webhookUrl?: string;
}

export interface WorkflowMutationOptions {
  originSocketId?: string | null;
}

/** SWR execution metadata from the backend endpoint. */
export interface EngineExecution {
  id: string;
  mode: string;
  startedAt: string;
  completedAt?: string | null;
  error?: string | null;
  status:
    | 'success'
    | 'error'
    | 'running'
    | 'waiting'
    | 'completed'
    | 'failed'
    | 'cancelled';
}

/** SWR-native step result from the execution endpoint. */
export interface EngineStepResult {
  nodeId: string | null;
  nodeName: string;
  nodeType: string;
  status: 'success' | 'error';
  durationMs: number;
  error?: string;
}

/** Execution detail with SWR-native step results. */
export interface EngineExecutionDetail extends EngineExecution {
  durationMs?: number | null;
  steps?: EngineStepResult[];
}

export type { AutomationSafetyDiagnosticPage };

// =============================================================================
// E007-T025: Workflow Schema Types
// =============================================================================

export interface WorkflowSchemaField {
  id: string;
  field: string;
  type:
    | 'attachment'
    | 'text'
    | 'rich-text'
    | 'number'
    | 'checkbox'
    | 'select'
    | 'multi-select'
    | 'date'
    | 'json';
  description: string;
  default?: unknown;
}

export interface WorkflowSchemaOutputField {
  id: string;
  name: string;
  type: string;
  description: string;
  required: boolean;
}

export interface WorkflowSchema {
  name: string;
  version: string;
  description?: string;
  input: {
    required: WorkflowSchemaField[];
    optional: WorkflowSchemaField[];
  };
  output: Record<string, WorkflowSchemaOutputField>;
}

export interface WorkflowPreflightSchemaField {
  field: string;
  type: string;
  description: string;
  source?: string;
}

export interface WorkflowPreflightFix {
  description: string;
  patches: Array<{
    nodeName: string;
    parameters: Record<string, unknown>;
  }>;
}

export interface WorkflowPublishPreflight {
  validationReport: ValidationReport;
  fixes: WorkflowPreflightFix[];
  expectedInput: WorkflowPreflightSchemaField[];
  expectedOutput: WorkflowPreflightSchemaField[];
  currentInput: WorkflowPreflightSchemaField[];
  currentOutput: WorkflowPreflightSchemaField[];
  sourceContracts: string[];
  clean: boolean;
}

export interface WorkflowDeleteUsage {
  configId: string;
  blockId: string;
  docId: string;
  /** Automation block title (human-readable) */
  automationName: string;
  /** @deprecated Use automationName instead */
  workflowName?: string;
}

export interface WorkflowDeleteImpact {
  workflowId: string;
  workflowName: string;
  usages: WorkflowDeleteUsage[];
}

export interface WorkflowAutomationUsage {
  configId: string;
  blockId: string;
  docId: string | null;
  automationName: string;
  canOpenDoc: boolean;
}

export interface WorkflowUsageImpact {
  workflowId: string;
  workflowName: string;
  usages: WorkflowAutomationUsage[];
}

export interface PublishNodeRequest {
  stepId: string;
  title: string;
  description: string;
  descriptionsI18n?: Record<string, string>;
  category: string;
  visibility?: 'GLOBAL' | 'WORKSPACE';
  previewImageBlobId?: string;
  templateId?: string;
  changelog?: string;
  slotDescriptionOverrides?: Array<{
    slotId: string;
    descriptionsI18n: Record<string, string>;
  }>;
}

export interface PublishNodeResponse {
  templateId: string;
  status: string;
  visibility: string;
  reviewMode: string;
  versionNumber: number;
}

// sl-xegl.10: Insert position suggestion response
export interface InsertSuggestion {
  recommended: {
    afterNodeName: string;
    outputIndex: number;
    reason: string;
  } | null;
  positions: Array<{
    afterNodeName: string;
    outputIndex: number;
    score: number;
    warnings: string[];
  }>;
  warnings: string[];
}

// Backend response types
interface WorkflowsResponse {
  workflows: WorkflowWithWebhook[];
}

interface WorkflowResponse {
  workflow: WorkflowWithWebhook | null;
}

// =============================================================================
// Service
// =============================================================================

const WORKFLOW_REQUEST_TIMEOUT_MS = 5000;

export class WorkflowClientService extends Service {
  /**
   * sl-n8i1: Workspace service for workspace-scoped workflow engine credentials
   */
  constructor(private readonly workspaceService: WorkspaceService) {
    super();
  }

  /**
   * sl-n8i1.3: Get current workspace ID
   */
  private get workspaceId(): string {
    return this.workspaceService.workspace.id;
  }

  /**
   * DRY URL builder for workflow API endpoints
   * Routes: /api/workspace/:workspaceId/workflow/*
   */
  private buildUrl(path: string): string {
    return `/api/workspace/${encodeURIComponent(this.workspaceId)}/workflow${path}`;
  }

  /**
   * URL builder for workflow-config endpoints
   * Routes: /api/workspace/:workspaceId/workflow-config/*
   */
  private buildConfigUrl(path = ''): string {
    return `/api/workspace/${encodeURIComponent(this.workspaceId)}/workflow-config${path}`;
  }

  private request(
    input: string | URL,
    init: RequestInit = {}
  ): Promise<Response> {
    const path = input.toString();
    const method = init.method ?? 'GET';
    const isMutation = method !== 'GET';
    return defaultWorkspaceApiClient.request({
      workspaceId: this.workspaceId,
      path,
      init,
      category: 'workspace-api',
      key: `${method}:${path}`,
      mode: isMutation ? 'foreground' : 'background',
      priority: isMutation ? 'foreground' : 'visible',
    });
  }

  private withMutationOrigin<TBody extends Record<string, unknown>>(
    body: TBody,
    options?: WorkflowMutationOptions
  ): TBody & { originSocketId?: string } {
    return options?.originSocketId
      ? { ...body, originSocketId: options.originSocketId }
      : body;
  }

  /**
   * sl-za0v: SWR is always configured — no provisioning needed.
   * Kept as LiveData for backwards compatibility with WorkflowService.isEngineConfigured$.
   */
  readonly isConfigured$ = new LiveData<boolean>(true);

  /**
   * sl-za0v: Always true — SWR needs no async status check.
   */
  readonly statusChecked$ = new LiveData<boolean>(true);

  /**
   * Error state
   */
  readonly error$ = new LiveData<string | null>(null);

  /**
   * Loading state
   */
  readonly isLoading$ = new LiveData<boolean>(false);

  /**
   * Cached workflows
   */
  readonly workflows$ = new LiveData<WorkflowWithWebhook[]>([]);

  /**
   * sl-za0v: SWR is always configured — no provisioning needed.
   */
  get isConfigured(): boolean {
    return true;
  }

  /**
   * Reset state on workspace change.
   */
  resetForWorkspaceChange(): void {
    this.workflows$.next([]);
    this.error$.next(null);
    this.isLoading$.next(false);
  }

  /**
   * sl-za0v: No-op — SWR needs no status check (always configured).
   * Kept for call-site compatibility (returns true immediately).
   */
  async checkStatus(): Promise<boolean> {
    return true;
  }

  /**
   * sl-1ahs.3.1: Lightweight health check via the central backend
   * availability manager.
   * Returns true if backend is reachable, false otherwise.
   * Used by WorkflowService.connectivity$ for polling.
   */
  async checkHealth(): Promise<boolean> {
    return probeBackendAvailabilityNow();
  }

  /**
   * Create a new empty workflow in workflow engine.
   */
  async createWorkflow(
    name: string,
    template?: string
  ): Promise<WorkflowWithWebhook | null> {
    try {
      const response = await this.request(this.buildUrl('/workflows'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, template }),
      });

      if (!response.ok) {
        console.warn(
          '[WorkflowClient] Failed to create workflow:',
          response.status
        );
        return null;
      }

      const data: { workflow: WorkflowWithWebhook } = await response.json();

      // Add to cache
      this.workflows$.next([...this.workflows$.value, data.workflow]);

      return data.workflow;
    } catch (error) {
      console.error('[WorkflowClient] Error creating workflow:', error);
      return null;
    }
  }

  /**
   * Fetch all workflows from backend proxy
   * sl-n8i1.3: Uses workspace-scoped route
   */
  async fetchWorkflows(): Promise<WorkflowWithWebhook[]> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const response = await this.request(this.buildUrl('/workflows'), {
        credentials: 'include',
        signal: AbortSignal.timeout(WORKFLOW_REQUEST_TIMEOUT_MS),
      });

      if (!response.ok) {
        const backendError = {
          status: response.status,
          message: response.statusText,
        };
        const backendUnavailable = isBackendUnavailableError(backendError);
        markBackendUnavailable(backendError);
        const message = `Backend error: ${response.status} ${response.statusText}`;
        if (!backendUnavailable) {
          console.error('[WorkflowClient] Failed to fetch workflows:', message);
        }
        this.error$.next(message);
        return [];
      }

      const data: WorkflowsResponse = await response.json();

      markBackendAvailable();
      this.workflows$.next(data.workflows);
      return data.workflows;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      if (!isBackendUnavailableError(error)) {
        console.error('[WorkflowClient] Failed to fetch workflows:', message);
      }
      markBackendUnavailable(error);
      this.error$.next(`network_error: ${message}`);
      return [];
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Get a specific workflow by ID
   * sl-n8i1.3: Uses workspace-scoped route
   */
  async getWorkflow(id: string): Promise<WorkflowWithWebhook | null> {
    // First check cache
    const cached = this.workflows$.value.find(
      (wf: WorkflowWithWebhook) => wf.id === id
    );
    if (cached) return cached;

    // Fetch from backend
    try {
      const response = await this.request(this.buildUrl(`/workflows/${id}`), {
        credentials: 'include',
      });

      if (!response.ok) {
        return null;
      }

      const data: WorkflowResponse = await response.json();
      return data.workflow;
    } catch (error) {
      console.error('[WorkflowClient] Failed to fetch workflow:', error);
      return null;
    }
  }

  /** sl-3yai.9: Cached node type → icon URL map (populated lazily on first workflow load) */
  private nodeTypeIconCache: Record<string, string> | null = null;

  /**
   * sl-3yai.9: Fetch icon URLs for node types used in a workflow.
   * Results are cached — subsequent calls with new types merge into the cache.
   */
  async fetchNodeTypeIcons(
    nodeTypes: Array<{ name: string; version: number }>
  ): Promise<Record<string, string>> {
    // Filter out types we already have cached
    const iconCache = this.nodeTypeIconCache;
    const uncached = iconCache
      ? nodeTypes.filter(nt => !(nt.name in iconCache))
      : nodeTypes;

    if (uncached.length > 0) {
      try {
        const response = await this.request(this.buildUrl('/node-type-icons'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nodeTypes: uncached }),
        });

        if (response.ok) {
          const data = (await response.json()) as {
            icons: Record<string, string>;
          };
          this.nodeTypeIconCache = {
            ...this.nodeTypeIconCache,
            ...data.icons,
          };
        }
      } catch (err) {
        console.warn('[WorkflowClient] Failed to fetch node type icons:', err);
      }
    }

    return this.nodeTypeIconCache ?? {};
  }

  /**
   * Get recent executions for a workflow
   * Note: Not yet implemented in backend proxy
   */
  async getExecutions(
    _workflowId: string,
    _limit = 5
  ): Promise<EngineExecution[]> {
    console.warn('[WorkflowClient] getExecutions not yet implemented');
    return [];
  }

  /**
   * Get latest execution for a workflow with step details
   * sl-n8i1.3: Uses workspace-scoped route
   */
  async getLatestExecution(
    workflowId: string
  ): Promise<EngineExecutionDetail | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/executions/latest`),
        { credentials: 'include' }
      );

      if (!response.ok) {
        console.warn(
          '[WorkflowClient] Failed to fetch execution:',
          response.status
        );
        return null;
      }

      // 204 No Content = no executions yet (expected, not an error)
      if (response.status === 204) {
        return null;
      }

      const data: { execution: EngineExecutionDetail | null } =
        await response.json();
      return data.execution;
    } catch (error) {
      console.error('[WorkflowClient] Error fetching execution:', error);
      return null;
    }
  }

  async getAutomationSafety(
    workflowId: string,
    configId: string
  ): Promise<AutomationSafetyDiagnosticPage | null> {
    try {
      const response = await this.request(
        this.buildUrl(
          `/workflows/${encodeURIComponent(workflowId)}/automations/${encodeURIComponent(configId)}/safety`
        ),
        { credentials: 'include' }
      );
      if (!response.ok) return null;
      return AutomationSafetyDiagnosticPageSchema.parse(await response.json());
    } catch (error) {
      console.error('[WorkflowClient] Safety preflight error:', error);
      return null;
    }
  }

  /**
   * sl-n8i1.3: Get workflow engine config status (masked info)
   * Used by settings panel to check current configuration.
   */
  async getEngineConfig(): Promise<{
    maskedBaseUrl: string;
    hasApiKey: boolean;
    verified: boolean;
    updatedAt: string;
  } | null> {
    try {
      const response = await this.request(this.buildConfigUrl(), {
        credentials: 'include',
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        console.warn('[WorkflowClient] Config fetch failed:', response.status);
        return null;
      }

      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch {
      return null;
    }
  }

  /**
   * sl-dpq2: Update workflow (rename, activate/deactivate)
   * Proxied through backend to workflow engine REST API.
   */
  async updateWorkflow(
    id: string,
    updates: {
      name?: string;
      active?: boolean;
      categories?: Array<{ id: string; name: string }>;
      inputSchema?: Record<string, unknown>;
      outputSchema?: Record<string, unknown>;
    }
  ): Promise<boolean> {
    try {
      const response = await this.request(this.buildUrl(`/workflows/${id}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        console.warn(
          '[WorkflowClient] Failed to update workflow:',
          response.status
        );
        return false;
      }

      // Invalidate cache
      const cached = this.workflows$.value;
      const idx = cached.findIndex((wf: WorkflowWithWebhook) => wf.id === id);
      if (idx >= 0) {
        const updated = { ...cached[idx], ...updates };
        const newList = [...cached];
        newList[idx] = updated;
        this.workflows$.next(newList);
      }

      return true;
    } catch (error) {
      console.error('[WorkflowClient] Error updating workflow:', error);
      return false;
    }
  }

  /**
   * List all workflow categories for current workspace.
   */
  async getCategories(): Promise<Array<{ id: string; name: string }>> {
    try {
      const response = await this.request(this.buildUrl('/categories'), {
        credentials: 'include',
        signal: AbortSignal.timeout(WORKFLOW_REQUEST_TIMEOUT_MS),
      });
      if (!response.ok) {
        markBackendUnavailable({
          status: response.status,
          message: response.statusText,
        });
        console.warn('[WorkflowClient] getCategories failed:', response.status);
        return [];
      }
      const data: { categories: Array<{ id: string; name: string }> } =
        await response.json();
      markBackendAvailable();
      return data.categories ?? [];
    } catch (err) {
      markBackendUnavailable();
      console.error('[WorkflowClient] getCategories error:', err);
      return [];
    }
  }

  /**
   * Create a new workflow category.
   */
  async createCategory(
    name: string
  ): Promise<{ id: string; name: string } | null> {
    try {
      const response = await this.request(this.buildUrl('/categories'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!response.ok) {
        console.warn(
          '[WorkflowClient] createCategory failed:',
          response.status,
          await response.text().catch(() => '')
        );
        return null;
      }
      const data: { category: { id: string; name: string } } =
        await response.json();
      return data.category;
    } catch (err) {
      console.error('[WorkflowClient] createCategory error:', err);
      return null;
    }
  }

  /**
   * Delete a workflow category.
   */
  async deleteCategory(categoryId: string): Promise<boolean> {
    try {
      const response = await this.request(
        this.buildUrl(`/categories/${categoryId}`),
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!response.ok) {
        console.warn(
          '[WorkflowClient] deleteCategory failed:',
          response.status
        );
        return false;
      }
      return true;
    } catch (err) {
      console.error('[WorkflowClient] deleteCategory error:', err);
      return false;
    }
  }

  /**
   * Get workflow delete impact (automations currently linked to this workflow).
   */
  async fetchWorkflowDeleteImpact(
    workflowId: string
  ): Promise<WorkflowDeleteImpact | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/delete-impact`),
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        console.warn(
          '[WorkflowClient] fetchWorkflowDeleteImpact failed:',
          response.status
        );
        return null;
      }

      return (await response.json()) as WorkflowDeleteImpact;
    } catch (error) {
      console.error(
        '[WorkflowClient] Error fetching workflow delete impact:',
        error
      );
      return null;
    }
  }

  /**
   * Get user-visible automation usages for a workflow.
   * Doc ids/titles are only returned when the current user can open that doc.
   */
  async fetchWorkflowUsages(
    workflowId: string
  ): Promise<WorkflowUsageImpact | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/usages`),
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) {
        console.warn(
          '[WorkflowClient] fetchWorkflowUsages failed:',
          response.status
        );
        return null;
      }

      return (await response.json()) as WorkflowUsageImpact;
    } catch (error) {
      console.error('[WorkflowClient] Error fetching workflow usages:', error);
      return null;
    }
  }

  /**
   * Delete a workflow via workflow engine REST API.
   */
  async deleteWorkflow(
    id: string,
    options?: { force?: boolean }
  ): Promise<boolean> {
    try {
      const params = new URLSearchParams();
      if (options?.force) params.set('force', 'true');
      const suffix = params.toString();
      const response = await this.request(
        this.buildUrl(`/workflows/${id}${suffix ? `?${suffix}` : ''}`),
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        console.warn(
          '[WorkflowClient] Failed to delete workflow:',
          response.status
        );
        return false;
      }

      // Remove from cache
      this.workflows$.next(
        this.workflows$.value.filter((wf: WorkflowWithWebhook) => wf.id !== id)
      );

      // Notify automation blocks that this workflow no longer exists
      console.log('[WorkflowClient] Dispatching workflow:deleted for', id);
      window.dispatchEvent(
        new CustomEvent('workflow:deleted', { detail: { workflowId: id } })
      );

      return true;
    } catch (error) {
      console.error('[WorkflowClient] Error deleting workflow:', error);
      return false;
    }
  }

  /**
   * Fetch workflow schema from the SWR backend endpoint.
   * Returns the input/output schema defined in the workflow definition.
   */
  async fetchWorkflowSchema(
    workflowId: string
  ): Promise<WorkflowSchema | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/schema`),
        { method: 'GET', credentials: 'include' }
      );

      if (!response.ok) return null;

      const data = await response.json();
      if (data.schema) {
        return data.schema as WorkflowSchema;
      }
      return null;
    } catch {
      return null;
    }
  }

  // ===========================================================================
  // sl-vzy8: Insert Node into Workflow
  // ===========================================================================

  /**
   * Remove a node from a workflow and rewire connections.
   */
  async removeNode(
    workflowId: string,
    stepId: string,
    options?: WorkflowMutationOptions
  ): Promise<{
    success: boolean;
    removedNodeName: string;
    validationReport?: ValidationReport;
    updatedWorkflow?: {
      id: string;
      name: string;
      active: boolean;
      nodes: unknown[];
      connections: Record<string, unknown>;
    };
    error?: string;
  } | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/remove-node`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.withMutationOrigin({ stepId }, options)),
        }
      );

      if (!response.ok) {
        console.warn('[WorkflowClient] Remove node failed:', response.status);
        const errorBody = await response.json().catch(
          () =>
            ({}) as {
              message?: string;
              error?: string;
              validationReport?: ValidationReport;
            }
        );
        return {
          success: false,
          removedNodeName: stepId,
          validationReport: errorBody.validationReport,
          error:
            errorBody.message || errorBody.error || `HTTP ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] Remove node error:', error);
      return null;
    }
  }

  // ===========================================================================
  // sl-dpq2.3: Version Management
  // ===========================================================================

  /**
   * List all version snapshots for a workflow.
   */
  async getWorkflowVersions(
    workflowId: string,
    limit?: number
  ): Promise<WorkflowVersion[]> {
    try {
      const query = limit ? `?limit=${limit}` : '';
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/versions${query}`),
        { credentials: 'include' }
      );
      if (!response.ok) return [];
      const data: { versions: WorkflowVersion[] } = await response.json();
      return data.versions;
    } catch (error) {
      console.error('[WorkflowClient] Error fetching versions:', error);
      return [];
    }
  }

  /**
   * Get a specific version snapshot.
   */
  async getWorkflowVersion(
    workflowId: string,
    version: number
  ): Promise<WorkflowVersion | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/versions/${version}`),
        { credentials: 'include' }
      );
      if (!response.ok) return null;
      const data: { version: WorkflowVersion } = await response.json();
      return data.version;
    } catch (error) {
      console.error('[WorkflowClient] Error fetching version:', error);
      return null;
    }
  }

  /**
   * Delete a local workflow snapshot version (non-published only).
   */
  async deleteWorkflowVersion(
    workflowId: string,
    version: number
  ): Promise<boolean> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/versions/${version}`),
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      return response.ok;
    } catch (error) {
      console.error('[WorkflowClient] Error deleting version:', error);
      return false;
    }
  }

  /**
   * Create a new draft snapshot from an existing stored version.
   * Does not mutate workflow engine until publish is confirmed.
   */
  async selectWorkflowVersionAsDraft(
    workflowId: string,
    version: number,
    userId?: string
  ): Promise<WorkflowVersion | null> {
    try {
      const response = await this.request(
        this.buildUrl(
          `/workflows/${workflowId}/versions/${version}/select-draft`
        ),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        }
      );
      if (!response.ok) return null;
      const data: { snapshot: WorkflowVersion } = await response.json();
      return data.snapshot;
    } catch (error) {
      console.error(
        '[WorkflowClient] Error selecting version as draft:',
        error
      );
      return null;
    }
  }

  /**
   * Commit draft as new current version (no workflow engine push, no activation).
   */
  async commitDraft(
    workflowId: string,
    userId?: string
  ): Promise<WorkflowVersion> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/commit`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      }
    );
    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        if (body?.message) detail = body.message;
      } catch {
        // response not JSON
      }
      throw new Error(`Commit failed: ${detail}`);
    }
    const data: { snapshot: WorkflowVersion } = await response.json();
    return data.snapshot;
  }

  /**
   * Publish (activate) a workflow.
   */
  async publishWorkflow(
    workflowId: string,
    userId?: string
  ): Promise<WorkflowVersion> {
    const response = await this.request(
      this.buildUrl(`/workflows/${workflowId}/publish`),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      }
    );
    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        if (body?.message) detail = body.message;
      } catch {
        // response not JSON
      }
      throw new Error(`Publish failed: ${detail}`);
    }
    const data: { snapshot: WorkflowVersion; warning?: string } =
      await response.json();
    if (data.warning) {
      // Attach warning so caller can display it
      (
        data.snapshot as WorkflowVersion & { _publishWarning?: string }
      )._publishWarning = data.warning;
    }
    return data.snapshot;
  }

  /**
   * Unpublish (deactivate) a workflow.
   */
  async unpublishWorkflow(
    workflowId: string,
    userId?: string
  ): Promise<WorkflowVersion | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/unpublish`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        }
      );
      if (!response.ok) return null;
      const data: { snapshot: WorkflowVersion } = await response.json();
      return data.snapshot;
    } catch (error) {
      console.error('[WorkflowClient] Error unpublishing workflow:', error);
      return null;
    }
  }

  /**
   * Discard draft snapshots for a workflow (server-side metadata only).
   */
  async discardWorkflowDraft(workflowId: string): Promise<boolean> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/discard-draft`),
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      return response.ok;
    } catch (error) {
      console.error('[WorkflowClient] Error discarding workflow draft:', error);
      return false;
    }
  }

  /**
   * Update workflow metadata by creating a draft snapshot (no direct workflow engine write).
   */
  async updateWorkflowDraftMetadata(
    workflowId: string,
    updates: {
      name?: string;
      categories?: Array<{ id: string; name: string }>;
    },
    userId?: string
  ): Promise<WorkflowVersion | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/draft-metadata`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...updates, userId }),
        }
      );
      if (!response.ok) return null;
      const data: { snapshot: WorkflowVersion } = await response.json();
      return data.snapshot;
    } catch (error) {
      console.error('[WorkflowClient] Error updating draft metadata:', error);
      return null;
    }
  }

  // ===========================================================================
  // sl-p6u0.4: Viewer State (Snapshot-based workflow loading)
  // ===========================================================================

  /**
   * Get the current viewer state for a workflow.
   * Returns latest snapshot (draft or published) + isDirty/isActive metadata.
   * Falls back to workflow engine if no snapshots exist (auto-migration).
   */
  async getWorkflowCurrent(workflowId: string): Promise<{
    workflow: WorkflowWithWebhook;
    /** SWR-native definition for the viewer (steps/edges/schemas) */
    swrDefinition: {
      id: string;
      workspaceId: string;
      name: string;
      steps: Array<Record<string, unknown>>;
      edges: Array<Record<string, unknown>>;
      inputSchema: Record<string, unknown>;
      outputSchema: Record<string, unknown>;
      schemaVersion: number;
    };
    source: 'draft' | 'published' | 'workflow engine';
    isDirty: boolean;
    publishedVersion: number | null;
    draftVersion: number | null;
    engineMissing?: boolean;
  } | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/current`),
        { credentials: 'include' }
      );

      if (!response.ok) return null;

      const data = await response.json();
      const wf = data.workflow;

      // Lightweight WorkflowWithWebhook for list compatibility
      const workflow: WorkflowWithWebhook = {
        id: wf.id,
        name: wf.name,
        active: wf.active,
        createdAt: '',
        updatedAt: '',
        categories: wf.categories,
        hasWebhook: false,
      } as WorkflowWithWebhook;

      // Sync cache: backend is authoritative for active/name/categories.
      // Without this, list-derived views (sidebar execution-check) stay stale
      // while the viewer shows correct state → divergent UI.
      const cached = this.workflows$.value;
      const idx = cached.findIndex(c => c.id === wf.id);
      if (idx >= 0) {
        const existing = cached[idx];
        if (
          existing.active !== wf.active ||
          existing.name !== wf.name ||
          JSON.stringify(existing.categories) !== JSON.stringify(wf.categories)
        ) {
          const newList = [...cached];
          newList[idx] = {
            ...existing,
            name: wf.name,
            active: wf.active,
            categories: wf.categories,
          };
          this.workflows$.next(newList);
        }
      }

      // SWR-native definition for the viewer
      const swrDefinition = {
        id: wf.id,
        workspaceId: this.workspaceId,
        name: wf.name,
        steps: wf.steps ?? [],
        edges: wf.edges ?? [],
        inputSchema: wf.inputSchema ?? { fields: [] },
        outputSchema: wf.outputSchema ?? { fields: [] },
        schemaVersion: 1,
      };

      return {
        workflow,
        swrDefinition,
        source: data.source,
        isDirty: data.isDirty,
        publishedVersion: data.publishedVersion,
        draftVersion: data.draftVersion,
        engineMissing: data.engineMissing,
      };
    } catch (error) {
      console.error('[WorkflowClient] Error fetching workflow current:', error);
      return null;
    }
  }

  // ===========================================================================
  // sl-dpq2.4: Patch Workflow (AI Subgraph Edit)
  // ===========================================================================

  /**
   * sl-vzy8.10: Update node positions in workflow engine (auto-layout).
   * positions: Record<nodeName, [x, y]>
   */
  async updateLayout(
    workflowId: string,
    positions: Record<string, [number, number]>,
    options?: WorkflowMutationOptions
  ): Promise<boolean> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/layout`),
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.withMutationOrigin({ positions }, options)),
        }
      );
      if (!response.ok) return false;
      const data: { success: boolean } = await response.json();
      return data.success;
    } catch {
      return false;
    }
  }

  /**
   * Apply a patch to a workflow: validate, merge, and PUT to workflow engine.
   * Returns validation report and updated workflow on success.
   */
  async patchWorkflow(
    workflowId: string,
    patch: {
      modifiedNodes: Array<{
        name: string;
        type: string;
        typeVersion?: number;
        position: [number, number];
        parameters?: Record<string, unknown>;
        credentials?: Record<string, unknown>;
        disabled?: boolean;
      }>;
      modifiedConnections: Record<string, unknown>;
      removedNodeNames: string[];
    }
  ): Promise<{
    success: boolean;
    validationReport: ValidationReport;
    updatedWorkflow?: unknown;
    error?: string;
  } | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/patch`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        }
      );

      if (!response.ok) {
        console.warn('[WorkflowClient] Patch failed:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] Patch error:', error);
      return null;
    }
  }

  async preflightWorkflowPublish(
    workflowId: string
  ): Promise<WorkflowPublishPreflight | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/preflight`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        console.warn('[WorkflowClient] Preflight failed:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] Preflight error:', error);
      return null;
    }
  }

  /**
   * Update a single node's input slots + config slots.
   *
   * sl-c9oi.6: `_slInputMappings` is gone. Bindings live inline
   * on `slot.value` as template strings (`{{steps.X.Y}}`) — the
   * runner resolves them in a single pass. `__inputSlots` / `__configSlots`
   * are typed pass-throughs; the dock's panel renderer writes
   * `NodeIOSlot[]` under these keys and the backend persists them directly
   * on `step.inputSlots` / `step.configSlots`.
   */
  async updateNode(
    workflowId: string,
    stepId: string,
    parameters: Record<string, unknown>,
    options?: WorkflowMutationOptions
  ): Promise<{
    success: boolean;
    updatedWorkflow?: unknown;
    error?: string;
  } | null> {
    try {
      const {
        __inputSlots,
        __configSlots,
        __outputSlots,
        __endMappings,
        __newName,
        ...configParams
      } = parameters;

      const body: {
        stepId: string;
        newName?: string;
        config?: Record<string, unknown>;
        inputSlots?: unknown[];
        configSlots?: unknown[];
        outputSlots?: unknown[];
        inputMappings?: Array<{
          sourceStepId: string;
          sourceFieldId: string;
          targetFieldId: string;
        }>;
      } = { stepId };

      if (typeof __newName === 'string') {
        body.newName = __newName;
      }

      if (Object.keys(configParams).length > 0) {
        body.config = configParams;
      }

      if (Array.isArray(__inputSlots)) {
        body.inputSlots = __inputSlots;
      }
      if (Array.isArray(__configSlots)) {
        body.configSlots = __configSlots;
      }
      if (Array.isArray(__outputSlots)) {
        body.outputSlots = __outputSlots;
      }
      if (Array.isArray(__endMappings)) {
        body.inputMappings = __endMappings as Array<{
          sourceStepId: string;
          sourceFieldId: string;
          targetFieldId: string;
        }>;
      }

      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/update-node`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.withMutationOrigin(body, options)),
        }
      );

      if (!response.ok) {
        console.warn('[WorkflowClient] Update node failed:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] Update node error:', error);
      return null;
    }
  }

  /**
   * Rename a node. Updates all connection references atomically.
   */
  async renameNode(
    workflowId: string,
    stepId: string,
    newName: string,
    options?: WorkflowMutationOptions
  ): Promise<{
    success: boolean;
    updatedWorkflow?: unknown;
    error?: string;
  } | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/rename-node`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            this.withMutationOrigin({ stepId, newName }, options)
          ),
        }
      );

      if (!response.ok) {
        console.warn('[WorkflowClient] Rename node failed:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] Rename node error:', error);
      return null;
    }
  }

  async publishNode(
    workflowId: string,
    payload: PublishNodeRequest
  ): Promise<PublishNodeResponse | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/publish-node`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
          const body = await response.json();
          if (body?.message) {
            detail = Array.isArray(body.message)
              ? body.message.join(', ')
              : String(body.message);
          }
        } catch {
          // response not JSON
        }
        throw new Error(`Node publish failed: ${detail}`);
      }

      return (await response.json()) as PublishNodeResponse;
    } catch (error) {
      console.error('[WorkflowClient] Publish node error:', error);
      throw error;
    }
  }

  /**
   * sl-7xgk.2.1: Insert a step into a workflow.
   * Returns the updated viewer workflow payload so callers can reconcile
   * local graph state without issuing a separate getViewerState() round trip.
   */
  async insertStep(
    workflowId: string,
    body: {
      afterStepId?: string;
      outputIndex?: number;
      splitEdgeId?: string;
      stepType: string;
      stepName?: string;
      stepConfig?: Record<string, unknown>;
      branchType?: 'branch_xor' | 'branch_or' | 'branch_and';
    },
    options?: WorkflowMutationOptions
  ): Promise<{
    success: boolean;
    insertedStepId: string;
    insertedStepName: string;
    updatedWorkflow?: unknown;
    error?: string;
  } | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/insert-node`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.withMutationOrigin(body, options)),
        }
      );

      if (!response.ok) {
        console.warn('[WorkflowClient] Insert step failed:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] Insert step error:', error);
      return null;
    }
  }

  /**
   * sl-7xgk.2.1: Relocate a node to a new position in the workflow's
   * topological order (atomic remove + re-insert on the backend).
   */
  async relocateNode(
    workflowId: string,
    body: { nodeId: string; afterStepId: string; outputIndex?: number },
    options?: WorkflowMutationOptions
  ): Promise<{
    success: boolean;
    updatedWorkflow?: unknown;
    error?: string;
  } | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/relocate-node`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.withMutationOrigin(body, options)),
        }
      );

      if (!response.ok) {
        console.warn('[WorkflowClient] Relocate node failed:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] Relocate node error:', error);
      return null;
    }
  }

  // ===========================================================================
  // sl-xegl.10: Insert Position Suggestions
  // ===========================================================================

  /**
   * Suggest optimal insert positions for a node type within a workflow.
   * Returns recommended position + all positions with scores and warnings.
   */
  async suggestInsertPositions(
    workflowId: string,
    nodeType: SWRStepType
  ): Promise<InsertSuggestion | null> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/suggest-insert-positions`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nodeType }),
        }
      );

      if (!response.ok) return null;

      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Parse SWR execution step results into nodeSteps format for logging.
   */
  parseExecutionToNodeSteps(execution: EngineExecutionDetail): Array<{
    name: string;
    nodeName: string;
    nodeType: string;
    status: 'success' | 'error';
    duration: number;
    itemCount: number;
    error?: string;
  }> {
    if (!execution.steps || execution.steps.length === 0) return [];

    return execution.steps.map(step => ({
      name: step.nodeName,
      nodeId: step.nodeId ?? undefined,
      nodeName: step.nodeName,
      nodeType: step.nodeType,
      status: step.status,
      duration: step.durationMs,
      itemCount: 0,
      error: step.error,
    }));
  }

  // ===========================================================================
  // sl-3yai.2.2: Marketplace API
  // ===========================================================================

  /**
   * sl-3yai.2.2: URL builder for marketplace endpoints
   * Routes: /api/workspace/:workspaceId/marketplace/*
   */
  private buildMarketplaceUrl(path: string): string {
    return `/api/workspace/${encodeURIComponent(this.workspaceId)}/marketplace${path}`;
  }

  /**
   * Search marketplace workflow templates with optional category filter and pagination.
   */
  async searchTemplates(
    query: string,
    category?: string,
    page?: number
  ): Promise<PaginatedResponse<WorkflowTemplate>> {
    try {
      const params = new URLSearchParams({ q: query });
      if (category) params.set('category', category);
      if (page !== undefined) params.set('page', String(page));

      const response = await this.request(
        this.buildMarketplaceUrl(`/templates?${params.toString()}`),
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(
          `Search templates failed: ${response.status} ${response.statusText}`
        );
      }

      // workflow engine public API returns { workflows, totalWorkflows } — transform to PaginatedResponse
      const raw: {
        workflows: Array<{
          id: number;
          name: string;
          description?: string;
          totalViews: number;
          createdAt: string;
          categories: Array<{ id: number; name: string }>;
          nodes: Array<{ type: string; displayName: string }>;
          user: { username: string };
        }>;
        totalWorkflows: number;
      } = await response.json();

      const pageSize = 20;
      const currentPage = page ?? 1;

      return {
        items: raw.workflows.map(w => ({
          id: String(w.id),
          name: w.name,
          description: w.description,
          author: w.user?.username,
          downloads: w.totalViews,
          categories: (w.categories ?? []).map(c => c.name),
          nodeTypes: (w.nodes ?? []).map(n => n.type),
          nodeCount: (w.nodes ?? []).length,
          createdAt: w.createdAt,
          updatedAt: w.createdAt,
        })),
        total: raw.totalWorkflows,
        page: currentPage,
        pageSize,
      };
    } catch (error) {
      console.error('[WorkflowClient] searchTemplates error:', error);
      throw error;
    }
  }

  /**
   * Fetch all marketplace categories with template counts.
   */
  async fetchCategories(): Promise<Category[]> {
    try {
      const response = await this.request(
        this.buildMarketplaceUrl('/categories'),
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(
          `Fetch categories failed: ${response.status} ${response.statusText}`
        );
      }

      // workflow engine public API returns { categories: [{id: number, name: string}] } — transform to Category[]
      const data: { categories: Array<{ id: number; name: string }> } =
        await response.json();
      return data.categories.map(c => ({
        id: String(c.id),
        name: c.name,
        count: 0,
      }));
    } catch (error) {
      console.error('[WorkflowClient] fetchCategories error:', error);
      throw error;
    }
  }

  /**
   * Analyze a marketplace template for compatibility before import.
   */
  async analyzeTemplate(templateId: string): Promise<CompatibilityEvaluation> {
    try {
      const response = await this.request(
        this.buildMarketplaceUrl(`/templates/${templateId}/analyze`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Analyze template failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] analyzeTemplate error:', error);
      throw error;
    }
  }

  /**
   * Analyze an existing workflow engine workflow for compatibility.
   */
  async analyzeExistingWorkflow(
    workflowId: string
  ): Promise<CompatibilityEvaluation> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/analyze`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Analyze workflow failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] analyzeExistingWorkflow error:', error);
      throw error;
    }
  }

  /**
   * Import a marketplace template into the workspace with optional auto-fix.
   */
  async importTemplate(
    templateId: string,
    options: ImportOptions
  ): Promise<ImportResult> {
    try {
      const response = await this.request(
        this.buildMarketplaceUrl(`/templates/${templateId}/import`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(options),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Import template failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] importTemplate error:', error);
      throw error;
    }
  }

  /**
   * Run schema discovery on a workflow to resolve dynamic node schemas.
   */
  async runDiscovery(workflowId: string): Promise<DiscoveryResult> {
    try {
      const response = await this.request(
        this.buildUrl(`/workflows/${workflowId}/discovery`),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Run discovery failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] runDiscovery error:', error);
      throw error;
    }
  }

  // ===========================================================================
  // sl-3yai.2.2: Copilot AI API (sl-3yai.6.2 + 6.3)
  // ===========================================================================

  /**
   * Classify an unknown node using AI copilot.
   */
  async classifyNodeWithAI(
    node: WorkflowEngineNode,
    context: WorkflowContext
  ): Promise<ClassificationSuggestion> {
    try {
      const response = await this.request(
        this.buildMarketplaceUrl('/copilot/classify-node'),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ node, context }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Classify node failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] classifyNodeWithAI error:', error);
      throw error;
    }
  }

  /**
   * Accept a classification suggestion, creating a persistent rule.
   */
  async acceptClassificationSuggestion(
    suggestion: ClassificationSuggestion
  ): Promise<ClassificationRuleDTO> {
    try {
      const response = await this.request(
        this.buildMarketplaceUrl('/copilot/accept-classification'),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ suggestion }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Accept classification failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error(
        '[WorkflowClient] acceptClassificationSuggestion error:',
        error
      );
      throw error;
    }
  }

  /**
   * Analyze migration feasibility for a node using AI copilot.
   */
  async analyzeMigrationWithAI(
    node: WorkflowEngineNode,
    context: WorkflowContext,
    findings: CompatibilityIssue[]
  ): Promise<CopilotMigrationResult> {
    try {
      const response = await this.request(
        this.buildMarketplaceUrl('/copilot/analyze-migration'),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ node, context, findings }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Analyze migration failed: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('[WorkflowClient] analyzeMigrationWithAI error:', error);
      throw error;
    }
  }
}
